/* eslint-disable @typescript-eslint/ban-types */
// allow describe.only so describeElement.only et. al. can be set up below
/* eslint-disable local-rules/deprecated-member-access */

import * as CssSelect from 'css-select';
import CssSelectBrowserAdapter from 'css-select-browser-adapter';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import mergeWith from 'lodash/mergeWith';
import pretty from 'pretty';
import * as sinon from 'sinon';
import {expect} from 'chai';
import {highlight as cliHighlight} from 'cli-highlight';
import type {Context} from 'mocha/lib/context';
import {produce} from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import {serializeAttributes} from 'common/util';
import {Store} from 'common/report/stores/store';
import {DeepPartial} from 'common/util/typescript';

import {restoreFetch} from '../util/mock-fetch';
import type {
  IsoDescribeElementInitFunc,
  IsoDescribeElementRenderFunc,
  IsoDescribeElementTestCasesFunc,
  IsoElementConfig,
  IsoElm,
  IsoRef,
  IsoTestConstruct,
} from './types';
import {CliColor} from './types'; // will be used as value, don't "import type"

/**
 * Return true if a given object is a ref.
 *
 * @param {IsoElm|Ref} elOrRef : object to check
 * @return {boolean} true if the object is a ref; false otherwise
 */
export function isRef(elOrRef: IsoElm | IsoRef): elOrRef is IsoRef {
  return Boolean(elOrRef?.isRef);
}

/**
 * From an element or ref object, extract the base DOM Element object.
 *
 * @param {IsoElm|Ref} elOrRef : object to get DOM Element from
 * @return {IsoElm|null} the base DOM Element from the given object
 */
function _getBaseIsoElm(elOrRef: IsoElm | IsoRef): IsoElm | null {
  return isRef(elOrRef) ? elOrRef.first() : elOrRef;
}

/**
 * From an element or ref object, extract the base DOM Element's shadowRoot object
 * (or `null` if it has no shadowRoot).
 *
 * @param {IsoElm|Ref} elOrRef : object to get DOM Element from
 * @return {ShadowRoot|null} the base DOM Element from the given object
 */
function _getBaseIsoElmShadowRoot(elOrRef: IsoElm | IsoRef): IsoElm | ShadowRoot | null {
  const el = _getBaseIsoElm(elOrRef);
  // for panel components, node.el refers to component shadowRoot (if useShadowDom=true)
  const shadowRoot = (el.getConfig && el.getConfig(`useShadowDom`) && el.el) || el.shadowRoot;
  return shadowRoot || null;
}

/**
 * From an element or ref object, extract the base DOM Element's list of
 * child elements, as well as the childen of its shadowRoot if one exists.
 *
 * @param {IsoElm|Ref} elOrRef : object to get DOM Element from
 * @return {IsoElm[]} the base DOM Element from the given object
 */
function _getBaseIsoElmChildren(
  elOrRef: IsoElm | IsoRef,
  {
    includeShadow = false,
  }: {
    includeShadow?: boolean;
  } = {},
): IsoElm[] {
  const el = _getBaseIsoElm(elOrRef);
  const shadowRoot = _getBaseIsoElmShadowRoot(el);
  return [
    ...Array.from((includeShadow && shadowRoot && shadowRoot.childNodes) || []),
    ...Array.from((el && el.childNodes) || []),
  ] as IsoElm[];
}

/**
 * Return true if tests are running in a Node JS environment; false otherwise.
 */
export function isNode() {
  // Avoid ReferenceError if `process` global is not defined whatsoever.
  // Note: Lodash's isUndefined does not protect against this.
  if (typeof process === `undefined`) {
    return false;
  }
  return !!process?.versions?.node;
}

/**
 * Return true if tests are running in a browser environment; false otherwise.
 */
export function isBrowser() {
  // Avoid ReferenceError if `window` global is not defined whatsoever.
  // Note: Lodash's isUndefined does not protect against this.
  if (typeof window === `undefined`) {
    return false;
  }
  return (
    !!window?.navigator?.userAgent && !window.navigator.userAgent.toLowerCase().includes(`node`)
    // in a Node.js environment, iso will set user agent to "Node.js"
  );
}

/**
 * Format html for logging to console
 *
 * @param {string|IsoElm|IsoRef} elOrRefOrStr : element or html string to output html for
 * @param {object} [options={}]
 * @param {boolean} [options.highlight=true]   : should html syntax be highlighted?
 * @param {boolean} [options.logHtml=true]     : should html be output to console?
 * @param {boolean} [options.logHtml=true]     : should html be output to console?
 * @param {string} [options.logLevel="log"]    : log level of console output
 * @param {string} [options.refAttr="elref"] : ref attr name for highlighting
 * @return {string} formatted html string
 */
export function printHtml(
  elOrRefOrStr: IsoElm | IsoRef | string,
  {
    highlight = true,
    logHtml = true,
    logLevel = `log`,
    refAttr = `elref`,
  }: {
    highlight?: boolean;
    logHtml?: boolean;
    logLevel?: string;
    refAttr?: string;
  } = {},
) {
  let html = pretty(htmlWithShadow(elOrRefOrStr));

  // highlight ref names in html
  const refRegex = new RegExp(`${refAttr}="([^"]+)"`, `g`);
  const refReplacement = `${refAttr}="${CliColor.Green}$1${CliColor.Reset}"`;
  html = highlight ? html.replace(refRegex, refReplacement) : html;

  // syntax highlight html
  html = highlight ? cliHighlight(html, {language: `html`}) : html;

  if (logHtml) {
    // eslint-disable-next-line no-console
    console[logLevel](html);
  }

  return html;
}

// TODO: Jordan - Dry out `mergeDeep`s and move to a higher level util
function overrideArrayCustomizer(targetVal, srcValue) {
  if (Array.isArray(targetVal)) {
    return srcValue;
  }
  // return undefined so default mergeWith behavior is used
  return undefined;
}

export function mergeDeepWithArrayOverride<T extends Obj>(...objects: Array<DeepPartial<T>>): DeepPartial<T> {
  const [targetObject, ...sourceObjects] = objects;
  return produce(targetObject, (draftObject: DeepPartial<T>): void => {
    if (sourceObjects?.length) {
      mergeWith(draftObject, ...sourceObjects, overrideArrayCustomizer);
    }
  });
}

function mergeArrayCustomizer(targetVal, srcValue) {
  if (Array.isArray(targetVal)) {
    return targetVal.concat(srcValue);
  }
  // return undefined so default mergeWith behavior is used
  return undefined;
}

export function mergeDeepWithArrayConcatenation<T extends Obj>(...objects: Array<DeepPartial<T>>): DeepPartial<T> {
  const [targetObject, ...sourceObjects] = objects;
  return produce(targetObject, (draftObject: DeepPartial<T>): void => {
    if (sourceObjects?.length) {
      mergeWith(draftObject, ...sourceObjects, mergeArrayCustomizer);
    }
  });
}

/**
 * Get element or ref outerHTML, including contents of any descendents with shadowRoots.
 * Normally shadow DOM content would be excluded from outerHTML; this is troublesome in
 * test code where you generally want to assert conditions on full html regardless of
 * shadow boundaries.
 *
 * @param {string|IsoElm|Ref} elOrRefOrStr : object to extract full html from
 * @param {object} [options={}]
 * @param {boolean} [options.fastUnordered=false] : perf optimization flag
 * @return {string} full outerHTML taken from input object
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function htmlWithShadow(
  elOrRefOrStr: IsoElm | IsoRef | string,
  {
    // optimization if order of html doesn't matter
    // e.g. waitForRender (only cares about presence of elements)
    fastUnordered = false,
  } = {},
): string {
  let html;

  if (isString(elOrRefOrStr)) {
    html = elOrRefOrStr;
  } else {
    const el = _getBaseIsoElm(elOrRefOrStr);
    html = el ? el.outerHTML : ``;

    if (html.length) {
      const nodes = [el];

      // begin DOM traversal
      while (nodes.length) {
        const node = nodes.shift();
        const shadowRoot = _getBaseIsoElmShadowRoot(node);

        if (shadowRoot) {
          if (fastUnordered) {
            html += shadowRoot.innerHTML;
          } else {
            const shadowHtml = `<shadow-root>${shadowRoot.innerHTML}</shadow-root>`;
            const nodeHtml = node.outerHTML;
            const nodeHtmlWithShadow = nodeHtml.replace(`>`, `>${shadowHtml}`);
            html = html.replace(nodeHtml, nodeHtmlWithShadow);
          }
        }

        // traverse DOM element children
        nodes.push(..._getBaseIsoElmChildren(node, {includeShadow: true}));
      }
    }
  }

  // strip <style> tags from resulting html; not relevant to tests and clutter debug output
  return html.replace(/<style[\s\S]*?style>/gi, ``);
}

/**
 * Initialize an element by tag name, updating its attr, prop, state data if requested.
 *
 * @param {string} elementTagName : the tag name of the element to be initialized
 * @param {object} [options={}]
 * @param {object|null} [options.attrs=null]   : attributes to update on the new element instance
 * @param {object|null} [options.props=null]   : properties to update on the new element instance
 * @param {object|null} [options.state=null]   : state data to update on the new element instance
 * @return {Ref} the new element instance, wrapped in a ref object for convenience
 */
export function initElement(elementTagName: string, {attrs = null, props = null, state = null} = {}): IsoRef {
  const el: IsoElm = document.createElement(elementTagName);
  return setElementData(el, {attrs, props, state});
}

const throwRenderErrorEvent = (ev: CustomEvent) => {
  const {detail} = ev;
  if (detail.error) {
    throw ev.detail.error;
  }
};

// this is an attempt to get more stable tests by waiting on renders at a known root
let rootElement = null;
let cleanUpRootElement = null;

/**
 * Append a provided element (or ref) instance to the DOM, and wait until it finishes rendering.
 * The element's `connectedCallback` will be executed implicitly when it is added to the DOM, before
 * rendering is complete.
 *
 * @param {IsoElm|Ref} elOrRef : the element instance to be rendered
 * @return {Promise<Ref>} a promise that will resolve with the element ref once it is fully rendered
 */
export function renderElement(elOrRef: IsoElm | IsoRef): Promise<IsoRef> {
  if (rootElement !== null) {
    // At this point, we have an existing element reference but don't expect to. We may have a situation where
    // there are multiple nested beforeEach blocks trying to set things up. In this case, we're going to attempt
    // to clean up the previous rootElement.

    // eslint-disable-next-line no-console
    console.log(`Root node was not null, attempting to DTRT.`);
    cleanUpRootElement?.();
  }
  const el = _getBaseIsoElm(elOrRef);
  rootElement = el;
  cleanUpRootElement = () => cleanUpElement(el);
  // "catch" any errors triggered by the element and throw them so tests properly fail
  el.addEventListener(`renderError`, throwRenderErrorEvent);

  // add el to dom - panel/isotests/dom-shims appendChild will execute el.connectedCallback
  document.body.appendChild(el);
  return waitForRender(el);
}

/**
 * Remove an element (or ref) instance from the DOM and perform other cleanup:
 *
 * - remove the element's `renderError` event handler
 * - verify element was registered an initialized; if not, show helpful errors
 *
 * @param {IsoElm|Ref} elOrRef : the element instance to be removed
 */
export function cleanUpElement(elOrRef: IsoElm | IsoRef) {
  const el = _getBaseIsoElm(elOrRef);

  el.removeEventListener(`renderError`, throwRenderErrorEvent);

  if (!el) {
    throw new Error(
      `Element not found during describeElement cleanup. Did you forget to call init within describeElement?`,
    );
  }

  rootElement = null;
  cleanUpRootElement = null;

  if (!el.disconnectedCallback) {
    throw new Error(
      `Element is not webcomponent as expected in describeElement. Did you forget to import and register the webcomponent class for ${
        el.tagName || el
      }?`,
    );
  }
}

/**
 * Remove all elements from the DOM and perform other cleanup to ensure
 * next suite/test begins with a clean DOM state:
 *
 * - remove all elements from the DOM (and invoke their disconnectedCallbacks)
 * - reset url hash
 */
export function cleanUpDom() {
  const nodes = [...Array.from(document.body.childNodes || [])];
  let node;
  let nodeIdx = 0;

  // collect all DOM nodes:
  while (nodeIdx < nodes.length) {
    node = nodes[nodeIdx++];
    nodes.push(..._getBaseIsoElmChildren(node as IsoElm, {includeShadow: true}));
  }

  if (cleanUpRootElement) {
    cleanUpRootElement();
  }
  // iterate in reverse so child disconnectedCallbacks are called before parents;
  // otherwise cleanup will not occur in expected order resulting in errors
  while (nodeIdx >= 0) {
    node = nodes[--nodeIdx];
    if (node && node.disconnectedCallback && node.initialized) {
      try {
        node.disconnectedCallback();
      } catch {
        // pass - handle disconnectedCallback errors during cleanup gracefully
      }
    }
  }

  // remove top-level nodes from document.body:
  Array.from(document.body.childNodes || []).forEach((node) => {
    document.body.removeChild(node);
  });

  window?.localStorage?.clear();
  // reset url hash
  window.location.hash = ``;
}

/**
 * Set attr, prop, and state data on element instance
 *
 * @param {IsoElm|Ref} elOrRef : the element instance to be updated
 * @param {object} options
 * @param {object|null} [options.attrs=null]   : attributes to update on the new element instance
 * @param {object|null} [options.props=null]   : properties to update on the new element instance
 * @param {object|null} [options.state=null]   : state data to update on the new element instance
 * @param {object|null} [options.appState=null]   : appState data to update on the new element instance
 * @return {Ref} the new element instance, wrapped in a ref object for convenience
 */
export function setElementData(
  elOrRef: IsoElm | IsoRef,
  {attrs = null, props = null, state = null, appState = null},
): IsoRef {
  const el = _getBaseIsoElm(elOrRef);

  if (attrs) {
    Object.entries(serializeAttributes(attrs)).forEach(([key, value]: [string, any]) => {
      if (value === null || value === undefined) {
        el.removeAttribute(key);
      } else {
        el.setAttribute(key, value);
      }
    });
  }

  if (props) {
    Object.entries(props).forEach(([key, value]: [string, any]) => {
      if (value === null || value === undefined) {
        delete el[key];
      } else {
        el[key] = value;
      }
    });
  }

  if (state) {
    el.update(state);
  }

  if (appState) {
    el.updateApp(appState);
  }

  return createRef(el);
}

/**
 * Set attr, prop, and state data on element instance,
 * execute test cases, then revert the updates.
 *
 * @param {IsoElm|Ref} elOrRef : the element instance to be updated
 * @param {object} options
 * @param {object|null} [options.attrs=null]   : attributes to update on the new element instance
 * @param {object|null} [options.props=null]   : properties to update on the new element instance
 * @param {object|null} [options.state=null]   : state data to update on the element instance
 * @param {Function} testCases : a function that executes desired test cases
 */
export async function withElementData(
  elOrRef: IsoElm | IsoRef,
  {attrs = null, props = null, state = null, appState = null},
  testCases: Function,
) {
  const el = _getBaseIsoElm(elOrRef);
  const originalAttrs = {};
  const originalProps = {};
  const originalState = el.state;
  const originalAppState = el.appState;

  if (props) {
    Object.keys(props).forEach((key) => {
      originalProps[key] = el[key];
    });
  }

  if (attrs) {
    Object.keys(attrs).forEach((key) => {
      originalAttrs[key] = el.getAttribute(key);
    });
  }

  setElementData(el, {attrs, props, state, appState});

  await waitForRender(el);
  await testCases(elOrRef);

  // revert
  setElementData(el, {
    attrs: originalAttrs,
    props: originalProps,
    state: originalState,
    appState: originalAppState,
  });

  await waitForRender(el);
}

/**
 * Set attr data on component instance,
 * execute test cases, then revert the updates.
 *
 * @param {Elm|Ref} elOrRef : the component instance to be updated
 * @param {object} attrs : attributes to update on the new component instance
 * @param {Function} testCases : a function that executes desired test cases
 */
export async function withAttrs(elOrIsoRef: IsoElm | IsoRef, attrs: Obj, testCases: Function) {
  await withElementData(elOrIsoRef, {attrs}, testCases);
}

/**
 * Set prop data on component instance,
 * execute test cases, then revert the updates.
 *
 * @param {IsoElm|IsoRef} elOrIsoRef : the component instance to be updated
 * @param {object} props : properties to update on the new component instance
 * @param {Function} testCases : a function that executes desired test cases
 */
export async function withProps(elOrIsoRef: IsoElm | IsoRef, props: Obj, testCases: Function) {
  await withElementData(elOrIsoRef, {props}, testCases);
}

/**
 * Set attr, prop, and state data on component instance,
 * execute test cases, then revert the updates.
 *
 * @param {IsoElm|IsoRef} elOrIsoRef : the component instance to be updated
 * @param {object} state : state data to update on the component instance
 * @param {Function} testCases : a function that executes desired test cases
 */
export async function withState(elOrIsoRef: IsoElm | IsoRef, state: Obj, testCases: Function) {
  await withElementData(elOrIsoRef, {state}, testCases);
}

/**
 * Set appState on component instance,
 * execute test cases, then revert the updates.
 *
 * @param {IsoElm|IsoRef} elOrIsoRef : the component instance to be updated
 * @param {object} appState : appState data to update on the component instance
 * @param {Function} testCases : a function that executes desired test cases
 */
export async function withAppState(elOrIsoRef: IsoElm | IsoRef, appState: Obj, testCases: Function) {
  await withElementData(elOrIsoRef, {appState}, testCases);
}

/**
 * Traverse element decendants (optionally including shadow DOM(s)) and return
 * all sub-elements matching given criteria:
 *
 * tagName ~ match sub-elements with the given tag name
 * attrName/Value ~ match sub-elements with the given attr name+value
 * selector ~ match sub-elements that match a selector string per css-select lib
 *
 * @param elOrRef : the element (or ref) to be searched
 * @param options
 * @param [options.tagName=null] : match sub-elements with the given tag name
 * @param [options.attrName=null] : match sub-elements with the given attr name/value
 * @param [options.attrValue=null] : match sub-elements with the given attr name/value
 * @param [options.selector=null] : match sub-elements that match a selector string
 * @param [options.index=null] : only return match at specific index (short-circuits DOM traversal once match is found)
 * @param [options.includeShadow=false] : if true, also search descendant shadow DOMs
 * @param an array of matching sub-elements
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function findElements(
  rootElOrRef: IsoElm | IsoRef,
  {
    tagName = null,
    attrName = null,
    attrValue = null,
    selector = null,
    node = null,
    index = null,
    includeShadow = false,
  }: {
    tagName?: string;
    attrName?: string;
    attrValue?: string;
    selector?: string;
    node?: Node;
    index?: number;
    includeShadow?: boolean;
  },
): IsoElm[] {
  const rootEl = _getBaseIsoElm(rootElOrRef);
  const candidates = [rootEl];
  let matches = [];
  const adapter = CssSelectBrowserAdapter;

  if (!attrValue && !tagName && !selector) {
    matches = [rootEl];
  }

  while (candidates.length) {
    const candidate = candidates.shift();

    let isMatch = false;

    if (!isMatch && attrValue) {
      isMatch = candidate.getAttribute && candidate.getAttribute(attrName) === attrValue;
    }

    if (!isMatch && tagName) {
      isMatch =
        candidate.tagName &&
        // in browser environment, tag names are uppercase; normalize for this
        candidate.tagName.toLowerCase() === tagName.toLowerCase();
    }

    if (!isMatch && selector) {
      // slow path
      isMatch = CssSelect.is(candidate, selector, {adapter});
    }

    if (!isMatch && node) {
      isMatch = candidate === node;
    }

    if (isMatch) {
      matches.push(candidate);

      if (index !== null && index >= 0 && index < matches.length) {
        break;
      }
    }

    candidates.push(..._getBaseIsoElmChildren(candidate, {includeShadow}));
  }

  if (index === null) {
    return matches; // null index: return full element array
  } else if (index >= 0 && index < matches.length) {
    return [matches[index]];
  } else if (index < 0 && index * -1 <= matches.length) {
    return [matches.slice(index)[0]]; // support negative indexing
  } else {
    return [];
  }
}

/**
 * throw error and print current el html if no ref was found at given index
 * (extremely useful for debugging)
 */
function _errorOnRefNotFound(refObj, refResults) {
  if (!refResults.length) {
    const error = `${CliColor.Red}matching element not found for ref ${CliColor.Blue}${
      refObj._refName || refObj._tagName || refObj._selector
    }${CliColor.Red}`;
    console.warn(`Error: ${error}`);
    console.warn(`Current HTML:${CliColor.Reset}`);
    // printHtml(refObj.root, {logLevel: `warn`});
    throw new Error(error);
  }

  return refResults;
}

/**
 * throw error and print current el html if multiple refs were found
 * (extremely useful for debugging)
 */
function _errorOnRefMultipleFound(refObj, refResults) {
  if (refResults.length > 1) {
    const error = `Multiple elements found matching ref ${CliColor.Blue}${
      refObj._refName || refObj._tagName || refObj._selector
    }${CliColor.Red}. You may want to use ref.all() or ref.first() instead of ref.el`;
    console.warn(`Error: ${error}`);
    console.warn(`Current HTML:${CliColor.Reset}`);
    // printHtml(refObj.root, {logLevel: `warn`});
    throw new Error(error);
  }

  return refResults;
}

/**
 * Search for child element(s) (or the element itself) matching given ref identifier.
 * Returns an intermediate ref object with a number of functions available:
 *   createRef(el, name).el         : return the **only** subelement matching name (error if muliple matches)
 *   createRef(el, name).first()    : return the first subelement matching name
 *   createRef(el, name).last()     : return the first subelement matching name
 *   createRef(el, name).nth(index) : return the nth subelement matching name
 *   createRef(el, name).all()      : return a list of all subelements matching name
 *   createRef(el, name).exists()   : return a boolean indicating whether any elements match name
 *   createRef(el, name).requery()  : clear the ref's results cache and requery elements from the DOM (chainable)
 *   createRef(el).tag(tagName)     : return a ref object that now references elements with the given tag name
 *   createRef(el).sel(selector)    : return a ref object that now references elements that match the given selector
 *   createRef(el).log()            : log ref to console without full DOM element output (huge)
 *
 * @param {IsoElm|Ref} rootElOrRef   : the root element to use as the scope of DOM queries
 * @param {string} [refName]      : a string used to idenfity DOM elements that match the ref
 * @param {string} [attr="elref"] : attribute marking a DOM element as a test "ref" element
 * @param {boolean} includeShadow : if true, also search descendant shadow DOMs
 * @return {Ref} the resulting ref object
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function createRef(
  rootElOrRef: IsoElm | IsoRef,
  refName: string | null = null,
  attr = `elref`,
  includeShadow = true,
): IsoRef {
  if (refName && /\W/.test(refName)) {
    throw new Error(
      `Provided ref name "${refName}" is invalid; must contain only alphanumeric characters and underscores.`,
    );
  }

  const _createRef = (root, {_refName, _tagName, _selector}) => {
    const refObj: IsoRef = {
      root,
      isRef: true,

      _refName,
      _tagName,
      _selector,
      _results: {},

      _get: (index = null): IsoElm[] => {
        if (!refObj._results[index]) {
          refObj._results[index] = findElements(refObj.root, {
            attrName: attr,
            attrValue: refObj._refName,
            tagName: refObj._tagName,
            selector: refObj._selector,
            index,
            includeShadow,
          });
        }
        return refObj._results[index];
      },

      _reset: (newRoot, {refName = null, tagName = null, selector = null} = {}) =>
        _createRef(newRoot, {
          _refName: refName,
          _tagName: tagName,
          _selector: selector,
        }),

      first: (): IsoElm => _errorOnRefNotFound(refObj, refObj._get(0))[0] || null,
      last: (): IsoElm => _errorOnRefNotFound(refObj, refObj._get(-1))[0] || null,
      nth: (index): IsoElm => _errorOnRefNotFound(refObj, refObj._get(index))[0] || null,
      exists: (index = 0): boolean => !!refObj._get(index).length,
      all: () => refObj._get(),

      requery: (newRoot = null) => refObj._reset(newRoot || refObj.root, refObj),
      ref: (refName) => refObj._reset(refObj.root, {refName}),
      tag: (tagName) => refObj._reset(refObj.root, {tagName}),
      sel: (selector) => refObj._reset(refObj.root, {selector}),

      attr: (key: string, val) => {
        const el = refObj.el;
        if (typeof val === `undefined`) {
          return el.getAttribute(key);
        } else {
          el.setAttribute(key, val);
          return refObj; // chainable
        }
      },

      isAttributeEnabled: (attr: string) => {
        const el = refObj.el;
        return el.hasAttribute(attr) && [``, `true`, attr.toLowerCase()].includes(el.getAttribute(attr).toLowerCase());
      },

      with: ({attrs = null, props = null, state = null}, testCases: Function) =>
        withElementData(refObj, {attrs, props, state}, testCases),

      el: null, // placeholder for "el" getter; otherwise typescript complains

      // debugging tools
      // log : log ref to console without full DOM element output (which is huge)
      log: () =>
        // eslint-disable-next-line no-console
        console.log(
          Object.assign({}, refObj, {
            root: refObj.root.tagName,
            el: (refObj.exists() && refObj.first().tagName) || null,
          }),
        ),
    };

    // define `el` getter:
    // similar to ref.first(), but throws error when multiple matches are found
    Object.defineProperty(refObj, `el`, {
      get: () => _errorOnRefNotFound(refObj, _errorOnRefMultipleFound(refObj, refObj.all()))[0],
    });

    // ref.with method - allow broad or specific syntax:
    // ref.with({attrs, props, state}, function () { ... });
    // ref.with.attrs(attrs, function () { ... });
    // etc.
    refObj.with.attrs = (attrs, testCases: Function) => withAttrs(refObj, attrs, testCases);
    refObj.with.props = (props, testCases: Function) => withProps(refObj, props, testCases);
    refObj.with.state = (state, testCases: Function) => withState(refObj, state, testCases);
    refObj.with.appState = (appState, testCases: Function) => withAppState(refObj, appState, testCases);

    return refObj;
  };

  return _createRef(_getBaseIsoElm(rootElOrRef), {
    _refName: refName,
    _tagName: null,
    _selector: null,
  });
}

/**
 * Await full rendering of given element(s)/ref(s) using cross-platform criteria - must work consistently across Node JS, Chrome, Firefox, etc.
 *
 * Returned promise resolves with last provided element/ref once render conditions are met.
 * Render is complete when, for each element provided (in turn):
 *   - `element.innerHTML` is not empty
 *   - `element.innerHTML` does not change between two animation frames
 *
 * Render checks are handled sequentially - element A must fully render before checks for element B begin.
 * This optimizes for the common use case where B is a sub-element of A and will not be present until A is rendered.
 *
 * `await waitForRender()` with no arguments results in a promise that resolves with null after 1-5 animation frames
 * depending on environment (running in browser 2-4 frames are added up front, see below).
 *
 * In browser, an extra 4 animation frames are added up front; this mitigates flake that only
 * appears in tests running on SauceLabs VMs (especially Firefox). Perf impact is negligible.
 * Exact number of frames has been adjusted based on flake observation over time.
 *
 * @param {...<IsoElm|Ref>} elsOrRefs : DOM element(s) or ref object(s) to await rendering of
 * @return {Promise<Ref|null>} promise which resolves on completed render with last provided element/ref
 */
export async function waitForRender(...elsOrRefs: Array<IsoElm | IsoRef>): Promise<IsoRef | null> {
  // wrap any provided base elements in refs
  const elRefs = (elsOrRefs.map((elOrRef) => (isRef(elOrRef) ? elOrRef : createRef(elOrRef))) as unknown) as IsoRef[];

  /*
    Attach local listeners for renderErrors (ref: media/js/onload-init.ts logPanelRenderErrors).
    here is an existing listener on the root component that calls `throwRenderErrorEvent`
    but in the node environment is not caught by mocha. Manually attach listeners to locally
    capture any errors.
   */
  //#region renderError setup
  const refs = elRefs.map((el) => _getBaseIsoElm(el));
  const errors = [];
  const localRenderThrow = (err) => {
    errors.push(err.detail.error);
  };
  refs.forEach((r) => r.addEventListener(`renderError`, localRenderThrow));

  if (isBrowser()) {
    // wait 4 extra frames to ensure render has started (better test reliability across various browsers)
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
  }

  let prevHtml;
  let currHtml;
  let elRef = elRefs.shift();
  let isRenderFinished = false;

  while (elRef && !isRenderFinished) {
    await new Promise(requestAnimationFrame);

    if (elRef.exists()) {
      prevHtml = currHtml;
      currHtml = htmlWithShadow(elRef.first(), {fastUnordered: true});
      isRenderFinished = currHtml && currHtml === prevHtml;

      // Additional logging to try and track down any flakes
      // render was marked finished, but domPatcher still has pending work
      if (isRenderFinished && (elRef.el?.domPatcher?.rendering || elRef.el?.domPatcher?.pending)) {
        isRenderFinished = false;
      }

      // if further elements were provided, reset isRenderFinished and
      // wait for each provided element to finish rendering in turn
      if (isRenderFinished && elRefs.length) {
        elRef = elRefs.shift();
        isRenderFinished = false;
      }
    }
  }

  // Tear down renderError listeners and throw if an error occurred
  //#region renderError tear down
  refs.forEach((r) => r.removeEventListener(`renderError`, localRenderThrow));
  if (errors.length) {
    console.error(`${errors.length} render error(s)`);
    throw errors[errors.length - 1];
  }
  //#endregion
  // return last provided element (wrapped in ref)
  return elRef || null;
}

/**
 * Similar to waitForRender but starts checking innerHTML of all elements/refs
 * simultaneously, and resolves once all of them are unchanging across 1 frame.
 *
 * @param {...<IsoElm|Ref>} elsOrRefs : DOM element(s) or ref object(s) to await rendering of
 * @return {Promise<Ref|null>} promise which resolves on completed render with last provided element/ref
 */
export async function waitForRenderParallel(...elsOrRefs: Array<IsoElm | IsoRef>): Promise<IsoRef | null> {
  // wrap any provided base elements in refs
  const elRefs = (elsOrRefs.map((elOrRef) => (isRef(elOrRef) ? elOrRef : createRef(elOrRef))) as unknown) as IsoRef[];

  if (isBrowser()) {
    // wait 4 extra frames to ensure render has started (better test reliability across various browsers)
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
  }

  let prevHtml;
  let currHtml;

  while (!currHtml || currHtml !== prevHtml) {
    await new Promise(requestAnimationFrame);

    if (elRefs.every((elRef) => elRef.exists())) {
      prevHtml = currHtml;
      currHtml = elRefs.reduce((html, elRef) => html + htmlWithShadow(elRef.first(), {fastUnordered: true}), ``);
    }
  }

  // return last provided element (wrapped in ref)
  return elRefs.slice(-1)[0] || null;
}

/**
 * Convenience wrapper for Element.dispatchEvent that accepts target DOM Element or ref object.
 *
 * @param {IsoElm|Ref} elOrRef                 : DOM element (or ref) to dispatch event from
 * @param {string|Event} eventTypeOrObject  : event type string or DOM event object to dispatch
 * @param {object} [eventDetail={}]         : detail data to be set on the dispatched event object
 * @return {Promise<Ref>} promise which resolves on completed render with last provided element/ref
 *
 * await dispatch(myRef, `custom-event-type`); // triggers 'custom-event-type' event using CustomEvent class for event object
 * await dispatch(myElement, `custom-event-type`, {foo: `bar`}); // triggers 'custom-event-type' event with detail {foo: `bar`}
 * await dispatch(myElement, new DeviceOrientationEvent(`deviceorientation`)); // triggers 'deviceorientation' event
 */
export async function dispatch(elOrRef: IsoElm | IsoRef, eventTypeOrObject, eventDetail = null): Promise<void> {
  let event;
  if (isString(eventTypeOrObject)) {
    event = new CustomEvent(eventTypeOrObject, eventDetail ? {detail: eventDetail} : {});
  } else if (!eventDetail) {
    event = eventTypeOrObject;
  } else {
    throw new Error(`Event.detail is read-only; pass detail while initializing Event object.`);
  }
  _getBaseIsoElm(elOrRef).dispatchEvent(event);
  // for convenience, we used to wait on render here, but ran into issues where dispatching an event on a element
  // could cause it to be removed or changed in the DOM, and then waitForRender on that element would throw because
  // it was no longer there
  if (rootElement) {
    await waitForRender(rootElement);
  }
}

/**
 * Util for simulating a click event
 *
 * @param {IsoElm|Ref} elOrRef                  : DOM element (or ref) to dispatch event from
 * @param {object} [eventAttrs={}]           : data to be set on the dispatched event object
 * @param {string} [eventAttrs.type="click"] : event type string
 * @return {Promise<Ref>} promise which resolves on completed render with last provided element/ref
 *
 * await click(myRef); // triggers 'click' event
 * await click(myElement, {type: `dblclick`}); // triggers 'dblclick' event
 * await click(myElement, {attr: `custom`}); // triggers 'click' event with custom attr set
 */
export async function click(
  elOrRef: IsoElm | IsoRef,
  eventAttrs: {type?: string; [key: string]: any} = {},
  ignoreDisabledAttr = false,
): Promise<void> {
  const {type = `click`, ...attrs} = eventAttrs;

  // only dispatch click event if element is not disabled; this emulates native behavior
  if (!createRef(elOrRef).isAttributeEnabled(`disabled`) || ignoreDisabledAttr) {
    await dispatch(elOrRef, new MouseEvent(type, attrs));
  }
}

/**
 * Util for simulating a keyboard event
 *
 * @param {IsoElm|Ref} elOrRef                    : DOM element (or ref) to dispatch event from
 * @param {string|object} [eventAttrs={}]      : data to be set on the dispatched event object, or KeyboardEvent 'code' string
 * @param {string} [eventAttrs.type="keydown"] : event type string
 * @return {Promise<Ref>} promise which resolves on completed render with last provided element/ref
 *
 * await keyboard(myRef, `Enter`); // triggers 'keydown' event with event.code === 'Enter'
 * await keyboard(myElement, {type: `keyup`, code: `Escape`}); // triggers 'keyup' event with event.code === 'Escape'
 * await keyboard(myElement, {attr: `custom`}); // triggers 'keydown' event with custom attr set (e.g. keyCode, key, etc.)
 */
export async function keyboard(
  elOrRef: IsoElm | IsoRef,
  eventAttrs: string | {type?: string; code?: string; [key: string]: any} = {},
): Promise<void> {
  eventAttrs = isString(eventAttrs) ? {code: eventAttrs} : eventAttrs;
  const {type = `keydown`, ...attrs} = eventAttrs;
  await dispatch(_getBaseIsoElm(elOrRef), new KeyboardEvent(type, attrs));
}

/**
 * Util for simulating an input event
 *
 * @param {IsoElm|Ref} elOrRef                  : DOM element (or ref) to dispatch event from
 * @param {string|object} [eventAttrs={}]    : data to be set on the dispatched event object, or 'value' string
 * @param {string} [eventAttrs.type="input"] : event type string
 * @param {any} [eventAttrs.value]           : event value
 * @return {Promise<Ref>} promise which resolves on completed render with last provided element/ref
 *
 * await input(myRef, `my input value`); // triggers 'input' event and sets myRef base element.value === `value`
 * await input(myElement, {type: `custom-input`, value: `my input value`}); // triggers 'custom-input' event and sets myElement.value === `value`
 * await input(myElement, {attr: `custom`}); // triggers 'input' event with custom attr set
 */
export async function input(
  elOrRef: IsoElm | IsoRef,
  eventAttrs: string | {type?: string; value?: string; [key: string]: any} = {},
): Promise<void> {
  await waitForRender(elOrRef);
  eventAttrs = isString(eventAttrs) ? {value: eventAttrs} : eventAttrs;
  const {type = `input`, value, ...attrs} = eventAttrs;
  const el = _getBaseIsoElm(elOrRef);
  // allow setting input/textarea values inline with `input` event dispatches
  // these actions should happen atomically, otherwise subtle test mis-ordering can lead to el.value being dropped, e.g.
  // input1.value = `foo`;
  // input2.value = `bar`;
  // dispatch(input1, `input`); // re-render
  // dispatch(input2, `input`); // input2.value may be overwritten due to re-render
  el.value = value;
  await dispatch(el, new Event(type, {...attrs, bubbles: true, composed: true}));
}

/**
 * Assert that a given element/html and substring meet a given condition,
 * with customized failure output that formats and highlights html instead
 * of printing it in a giant block that's horrible to read (default Chai behavior).
 * Note: Chai still appends the original error output at the end, I could not find
 *       a way to avoid it. Seems helpful in some situations so will leave for now.
 *
 * Example usage:
 * expectHtml(`<div class="kitten"><p>kitten</p></div>`, `to include`, `kittens`);
 * -->
 * AssertionError: Expected
 *   <div class="kitten">    // html syntax will be highlighted in console
 *     <p>
 *       kitten
 *     </p>
 *  </div>
 * to include
 * "kittens"
 *
 * @param {string|IsoElm|IsoRef} elOrRefOrStr : element/html to run assertion on
 * @param {string} assertion="to include" : describes type of assertion to make
 * @param {string} subString              : substring to use in assertion
 */
export function expectHtml(
  elOrRefOrStr: IsoElm | IsoRef | string,
  assertion = `to include`, // default val included as example
  value: string | string[],
) {
  const html = htmlWithShadow(elOrRefOrStr);
  const words = assertion.split(` `);
  const last = words.pop();
  const expression = words.reduce((exp, word) => exp[word], expect(html));
  const subStrings = value instanceof Array ? value : [value];

  for (const subString of subStrings) {
    expression[last](
      subString,
      `Expected
         ${printHtml(html, {logHtml: false}).split(`\n`).join(`\n       `)}
       ${CliColor.Red}${assertion}${CliColor.Reset}
       "${subString}"

Raw Chai expect output:
`,
    );
  }
}

/**
 * Creates wrapper around Mocha describe/context/it that provides node and browser variants.
 *
 * @param {object} [config]
 * @param {boolean} [config.node] : only run test cases if currently in node environment
 * @param {boolean} [config.browser]  : only run test cases if currently **not** in node environment
 * @param {boolean} [config.only] : only run these test cases (Mocha describe.only)
 * @param {boolean} [config.skip] : don't run these tests cases (Mocha describe.skip)
 * @return {Describe}
 */
export function buildTestConstruct(
  constructName: string,
  construct,
  {node = true, browser = true, only = false, skip = false} = {},
): IsoTestConstruct {
  if (!construct) {
    return function () {
      throw new Error(
        `Base ${constructName} not available; please call iso.register.${constructName}(<${constructName}>) before using it in your tests`,
      );
    };
  }

  const isNodeEnv = isNode();
  let cons;

  skip = skip || (!node && isNodeEnv) || (!browser && !isNodeEnv);
  cons = construct;
  cons = only ? construct.only : cons;
  cons = skip ? construct.skip : cons;

  // solve some occasional issues in browser where `describe` isn't defined yet
  cons =
    cons ||
    function () {
      /* pass */
    };

  // clone original func so original isn't modified when we assign
  // describe.node, describe.browser, etc.
  cons = cons.bind(construct);

  return (cons as unknown) as IsoTestConstruct;
}

/**
 * Creates wrapper around Mocha describe that renders and tears down an element appropriately
 * before and after each test case, as well as providing node and browser variants.
 *
 * @param {object} [config]
 * @param {boolean} [config.node] : only run test cases if currently in node environment
 * @param {boolean} [config.browser]  : only run test cases if currently **not** in node environment
 * @param {boolean} [config.only] : only run these test cases (Mocha describe.only)
 * @param {boolean} [config.skip] : don't run these tests cases (Mocha describe.skip)
 * @return {Describe}
 */
export function buildDescribeElement({node = true, browser = true, only = false, skip = false} = {}): IsoTestConstruct {
  return (description: string, testCases: IsoDescribeElementTestCasesFunc) => {
    const describeFunc = buildTestConstruct(`describe`, describe, {node, browser, only, skip});

    describeFunc(description, function (this: Context) {
      let element;

      const initFunc: IsoDescribeElementInitFunc = (...elementConfigs: Array<string | IsoElementConfig>) => {
        const cleanedElementConfigs = elementConfigs.map((objOrString) =>
          isString(objOrString) ? {tagName: objOrString} : objOrString,
        );
        const invalidConfig = cleanedElementConfigs.find((config) => !isObject(config));
        if (invalidConfig) {
          throw new Error(`Invalid config provided: ${invalidConfig}`);
        }

        // Todo: Better type the utilities
        const {tagName, ...elementConfig} = mergeDeepWithArrayConcatenation(...cleanedElementConfigs);

        if (!isString(tagName)) {
          throw new Error(`Invalid tagName provided: ${elementConfig}`);
        } else if (!tagName.length) {
          throw new Error(`Missing tagName in config; call init(<tag-name>, <config>)`);
        }

        if (element) {
          cleanUpElement(element);
        }
        cleanUpDom();

        element = initElement(tagName, elementConfig);

        return element;
      };

      const renderFunc: IsoDescribeElementRenderFunc = renderElement;

      let fetchMockedExternally;
      before(() => {
        fetchMockedExternally = !!(
          // @ts-ignore
          // eslint-disable-next-line no-prototype-builtins
          (global?.fetch?.hasOwnProperty?.(`restore`) || window?.fetch?.hasOwnProperty?.(`restore`))
        );
      });

      testCases.call(this, initFunc, renderFunc);

      after(() => {
        cleanUpElement(element);
        cleanUpDom();

        // clean up fetch mocking automatically if it was done within describeElement
        !fetchMockedExternally && restoreFetch();
      });
    });
  };
}

export const describeElement = buildDescribeElement();
describeElement.node = buildDescribeElement({browser: false});
describeElement.browser = buildDescribeElement({node: false});
describeElement.only = buildDescribeElement({only: true});
describeElement.skip = buildDescribeElement({skip: true});
describeElement.node.only = buildDescribeElement({browser: false, only: true});
describeElement.node.skip = buildDescribeElement({browser: false, skip: true});
describeElement.browser.only = buildDescribeElement({node: false, only: true});
describeElement.browser.skip = buildDescribeElement({node: false, skip: true});

/**
 * Mock a store's value and subscriptions with fixture data so store.fetch will not be called
 *
 * @param {object} handlers : map of urls<>handlers that will be passed to FetchServer
 */
// export function mockStore<T>(store: Store<T>, dataObj: T)
export async function mockStore<T, RT = T>(store: Store<T, RT>, dataObj: DeepPartial<RT>) {
  await restoreStore(store);

  // @ts-ignore // We want to mess with the internal transform method
  const storeTransform = store.transform.index;
  let storeData;
  if (storeTransform) {
    storeData = storeTransform(cloneDeep(dataObj) as RT, {store});
  } else {
    storeData = cloneDeep(dataObj);
  }

  sinon.stub(store, `value`).value(storeData);

  // Stub store.fetch so that element calls to it will immediately invoke
  // the corresponding element subscription callbacks with store.value
  // provided above, instead of attempting a window.fetch for a new value.
  const fetchSet = new Set<string>();
  sinon.stub(store, `fetch`).callsFake(
    // @ts-ignore Type 'void' is not assignable to type 'Promise<void>'
    (_params, _options, key = ``) => {
      fetchSet.add(key);
      return store.fireCallbacks(key);
    },
  );
  sinon.stub(store, `hasFetchedValue`).callsFake((key = ``) => fetchSet.has(key));
}

/**
 * Mock a store's fetch & refresh with fixture data so they don't use the network.
 * Useful for mocking stores that rely on params.
 */
export async function mockStoreWithFetch<T>(
  store: Store<T>,
  getResult: (params: object, options: object, key: string) => Promise<any>,
) {
  await restoreStore(store);

  const storeFetch = async (params, options, key = ``): Promise<void> => {
    const subscription = store.getSubscription(key);
    if (!subscription.initialized) {
      subscription.initialized = true;
      subscription.params = params;
      subscription.options = options;
    }

    const storeTransform = store[`transform`].index;
    try {
      const result = await getResult(params, options, key);
      const value: any = storeTransform ? storeTransform(result, {subscription, store}) : result;
      store._update(value as Partial<T>);
      store.fireCallbacks(key);
    } catch (err) {
      store.fireCallbacks(key, {error: true});
    }
  };
  sinon.stub(store, `fetch`).callsFake(storeFetch);
  sinon.stub(store, `refresh`).callsFake(storeFetch);
}

/**
 * Attempt to restore stubbed store value and fetch
 */
export async function restoreStore(store: Store<any>) {
  let hasBeenMocked = false;
  if (store.value !== null && Object.prototype.hasOwnProperty.call(store.value, `restore`)) {
    store.value.restore();
    hasBeenMocked = true;
  }
  if (Object.prototype.hasOwnProperty.call(store.fetch, `restore`)) {
    // @ts-ignore
    store.fetch.restore();
    hasBeenMocked = true;
  }
  if (Object.prototype.hasOwnProperty.call(store.hasFetchedValue, `restore`)) {
    // @ts-ignore
    store.hasFetchedValue.restore();
    hasBeenMocked = true;
  }
  if (hasBeenMocked) {
    await store.reset();
  }
}

// If needed, use this error filter by assigning it to window.uncaughtErrorFilter, eg:
//   window.uncaughtErrorFilter = commonUncaughtErrorFilter;
export function commonUncaughtErrorFilter(uncaughtError: ErrorEvent): boolean {
  // Firefox can throw this useless error when running ResizeObserver in WCT which will confusingly break tests,
  // this error should be benign and can be ignored (https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded#comment86691361_49384120)
  if (uncaughtError.message === `ResizeObserver loop completed with undelivered notifications.`) {
    return true;
  }
  return false;
}
